import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { StatusBadge } from '../components/StatusBadge';
import { formatCost, formatDate, formatInt, formatLatency } from '../format';

// Usage → Web search (task search_usage_ledger, ADR 0079). The search half of
// total AI spend, the sibling of the LLM spend page: calls, billable queries,
// candidate results, estimated cost, and error rate for the Serper / Tavily
// searches the manual crawl runs, aggregated by provider over a selectable
// window, plus the most recent searches. Mirrors AdminLlmUsage on purpose so an
// operator who knows one page knows both — cost leads, the per-call log is
// collapsed debugging detail.

interface UsageGroup {
  key: string;
  calls: number;
  errors: number;
  queries: number;
  results: number;
  costUsd: number;
  avgLatencyMs: number;
}

interface RecentSearch {
  id: string;
  created_at: string;
  query: string | null;
  provider: string;
  status: 'ok' | 'error';
  query_count: number;
  result_count: number | null;
  cost_usd: number | null;
  latency_ms: number | null;
  http_status: number | null;
  error: string | null;
  crawl_run_id: string | null;
}

interface UsageSummary {
  windowDays: number;
  totals: {
    calls: number;
    errors: number;
    queries: number;
    results: number;
    costUsd: number;
    avgLatencyMs: number;
  };
  byProvider: UsageGroup[];
  recent: RecentSearch[];
  everUsed: boolean;
}

const WINDOWS = [7, 30, 90];

// 'serper' → 'Serper'. The ledger stores providers lower-case; display them
// capitalized without hard-coding the set (a new provider needs no change here).
function titleCase(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

// Map a search outcome to the shared badge: ok → the succeeded (teal) tint,
// error → the failed (red) tint, keeping the literal "ok"/"error" as the label
// via StatusBadge's label prop (the class drives colour, the label drives text).
function statusTone(status: 'ok' | 'error'): string {
  return status === 'ok' ? 'succeeded' : 'failed';
}

export function AdminSearchUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-call log is operator-debugging detail — collapsed by default so the page
  // leads with the cost + provider breakdown an operator scans for spend.
  const [showRecent, setShowRecent] = useState(false);

  useEffect(() => {
    // Guard against out-of-order resolution: clicking through windows quickly
    // fires overlapping requests, and a slower earlier one must not overwrite
    // the newer window's data.
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<UsageSummary>(`/v1/admin/search-usage?days=${days}`)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load web search usage.');
      });
    return () => {
      ignore = true;
    };
  }, [days]);

  const hasRows = data !== null && data.totals.calls > 0;

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Web search usage</h1>
        <span className="admin-count admin-seg">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className="admin-link-button"
              disabled={w === days}
              onClick={() => setDays(w)}
            >
              {w}d
            </button>
          ))}
        </span>
      </header>
      <p className="admin-hint">
        Web searches the AI manual crawl ran to find PDF manuals (Serper / Tavily), over the last{' '}
        {days} days.
      </p>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null && !hasRows ? (
        data.everUsed ? (
          // Rows exist, just not in this window. Calling this "no searches yet"
          // would be a lie. Nudge to a wider window only when one is actually
          // selectable — at the widest window that advice would dead-end.
          <p className="admin-empty">
            No searches in the last {days} days.
            {days < Math.max(...WINDOWS)
              ? ' Try a longer window.'
              : ' Older searches may exist outside this window.'}
          </p>
        ) : (
          // The ledger is empty: a get-started state pointing at the action that
          // produces data, not a wall of zeroes.
          <div className="admin-empty-state">
            <p className="admin-empty-state-title">No searches yet</p>
            <p className="admin-hint">
              The manual crawl hasn’t run any web searches. Start a crawl from{' '}
              <Link to="/admin/manuals">Manual review</Link>, and its Serper / Tavily searches will
              show up here with their estimated cost.
            </p>
          </div>
        )
      ) : null}

      {hasRows && data !== null ? (
        <>
          <dl className="admin-defs">
            <dt>Searches</dt>
            <dd>
              {formatInt(data.totals.calls)}
              {data.totals.errors > 0 ? ` (${formatInt(data.totals.errors)} errors)` : ''}
            </dd>
            <dt>Queries</dt>
            <dd>{formatInt(data.totals.queries)}</dd>
            <dt>Results</dt>
            <dd>{formatInt(data.totals.results)}</dd>
            <dt>Avg latency</dt>
            <dd>{formatLatency(data.totals.avgLatencyMs)}</dd>
            {/* Estimated cost is the page's lead figure — enlarged via
                .admin-defs-lead so spend dominates by size, not position. */}
            <dt>Estimated cost</dt>
            <dd className="admin-defs-lead">{formatCost(data.totals.costUsd)}</dd>
          </dl>
          <p className="admin-defs-note">
            Estimated from each provider’s published per-query price — neither returns a billed
            amount.
          </p>

          <h2>By provider</h2>
          {data.byProvider.length === 0 ? (
            <p className="admin-empty">No searches in this window.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Searches</th>
                    <th>Queries</th>
                    <th>Queries / search</th>
                    <th>Results</th>
                    <th>Errors</th>
                    <th>Avg latency</th>
                    <th>Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byProvider.map((g) => (
                    <tr key={g.key}>
                      <td>{titleCase(g.key)}</td>
                      <td>{formatInt(g.calls)}</td>
                      <td>{formatInt(g.queries)}</td>
                      {/* Queries/search makes Serper's split-query fan-out legible
                        (>1) vs Tavily's always-1. g.calls > 0 for any group row. */}
                      <td>{(g.queries / g.calls).toFixed(1)}</td>
                      <td>{formatInt(g.results)}</td>
                      {/* Quiet plain "0" when healthy (matches the LLM page's
                        numeric Errors column — the sibling this page mirrors); a
                        red badge only when there's something to notice. */}
                      <td>
                        {g.errors > 0 ? (
                          <StatusBadge status="failed" label={`${formatInt(g.errors)} errors`} />
                        ) : (
                          '0'
                        )}
                      </td>
                      <td>{formatLatency(g.avgLatencyMs)}</td>
                      <td>{formatCost(g.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>
            Recent searches{' '}
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setShowRecent((v) => !v)}
            >
              {showRecent ? 'Hide' : 'Show'}
            </button>
          </h2>
          {showRecent ? (
            data.recent.length === 0 ? (
              <p className="admin-empty">No searches in this window.</p>
            ) : (
              <div className="scroll-x">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Provider</th>
                      <th>Query</th>
                      <th>Status</th>
                      <th>Queries</th>
                      <th>Results</th>
                      <th>Est. cost</th>
                      <th>Latency</th>
                      <th>Crawl run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c) => (
                      <tr key={c.id}>
                        <td>{formatDate(c.created_at)}</td>
                        <td>{titleCase(c.provider)}</td>
                        <td>
                          <span className="admin-usage-inline" title={c.query ?? undefined}>
                            {c.query ?? '—'}
                          </span>
                        </td>
                        {/* Full error text rides in title= so a failure is
                          diagnosable on hover without a dedicated column. */}
                        <td title={c.error ?? undefined}>
                          <StatusBadge status={statusTone(c.status)} label={c.status} />
                          {c.status === 'error' && c.http_status !== null
                            ? ` (${c.http_status})`
                            : ''}
                        </td>
                        <td>{formatInt(c.query_count)}</td>
                        <td>{c.result_count !== null ? formatInt(c.result_count) : '—'}</td>
                        <td>{formatCost(c.cost_usd)}</td>
                        <td>{formatLatency(c.latency_ms)}</td>
                        {/* 8-char stub for scanning; full id on hover to correlate
                          with a crawl run (the run itself lives on Manual review). */}
                        <td>
                          {c.crawl_run_id !== null ? (
                            <code title={c.crawl_run_id}>{c.crawl_run_id.slice(0, 8)}</code>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </>
      ) : null}
    </section>
  );
}
